/**
 * Works out what a `delivery-failed` case actually needs, from the pasted
 * payload alone.
 *
 * `delivery-failed` covers six internal reasons, and APP-2426 constraint 2
 * keeps every one of them out of the message body — so the code the operator
 * can see does not say which they have. Two independent questions decide the
 * action, and the payload answers both, so the form can say something useful
 * instead of listing six situations and hoping.
 *
 * **Not authoritative.** This duplicates backend knowledge across two repos:
 * if a seventh internal reason ships with a new precondition, nothing here
 * fails, it just quietly says the wrong thing. The notification's own
 * *Next step* line is the authority, and the copy says so.
 *
 * Pure. Returns a key, never display text.
 */
import { SALESFORCE_ACCOUNT_ID_PATTERN } from '@/features/SalesforceAccounts/lib/salesforceOutcome'
import { readResolutionFields } from '@/features/SalesforceIngest/lib/parsePayloadBlock'
import type { SalesforceOpportunityIngestRequest } from '@/generated/http-clients/v3ApiSchemas'

/**
 * The four situations, named by what CS does rather than by which service
 * broke. Which service was unreachable does not change the action.
 */
export type DeliveryFailedAdvice =
  /** From Salesforce, and the account id is wrong: correct it there first. */
  | 'ingestMalformed'
  /** From Salesforce, id fine: re-send unchanged once healthy. */
  | 'ingestTransient'
  /** From a resolution, id wrong: correct in Salesforce, re-submit here. */
  | 'resolveMalformed'
  /** From a resolution, id fine: re-submit the same resolution here. */
  | 'resolveTransient'

/**
 * Did this payload come from a previous resolution rather than the Salesforce
 * ingest?
 *
 * `SalesforceOpportunityResolveRequest` is `allOf: [IngestRequest, {required:
 * [createNewOrg, createNewAccount, reason]}]`, a strict superset, and the
 * backend injects those fields whenever the case came from the resolve
 * endpoint. It branches on the same value to build the message, so the form
 * and the message cannot disagree.
 *
 * Keyed on the two booleans, **never** on `orgId`: that one is conditional,
 * written only when non-null, and legitimately absent whenever `createNewOrg`
 * is true.
 */
export function isResolveOriginated(raw: string): boolean {
  return readResolutionFields(raw) !== null
}

/**
 * Is the Salesforce account id the one shape that has a precondition?
 *
 * Reuses `SALESFORCE_ACCOUNT_ID_PATTERN` rather than restating it: that
 * constant is byte-identical to the backend's `SF_ACCOUNT_ID_PATTERN`, and a
 * third copy of a regex three places already agree on is how they stop
 * agreeing. Trimmed first, as its docblock requires.
 *
 * Of the six internal reasons behind `delivery-failed`, this is the only one
 * with a precondition: re-driving without correcting the id in Salesforce
 * fails identically. The other five are transient and share one action.
 */
export function hasMalformedAccountId(
  payload: SalesforceOpportunityIngestRequest,
): boolean {
  return !SALESFORCE_ACCOUNT_ID_PATTERN.test(payload.salesforceAccountId.trim())
}

export function deliveryFailedAdvice(
  payload: SalesforceOpportunityIngestRequest,
  raw: string,
): DeliveryFailedAdvice {
  const fromResolve = isResolveOriginated(raw)
  const malformed = hasMalformedAccountId(payload)

  if (fromResolve) {
    return malformed ? 'resolveMalformed' : 'resolveTransient'
  }
  return malformed ? 'ingestMalformed' : 'ingestTransient'
}
